// src/features/tests/TestBuilder.js
import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './TestBuilder.css';

const TestBuilder = ({ onTestCreated }) => {
  const [step, setStep] = useState(1); // 1: Basic info, 2: Question selection (diagnostic only)
  const [diagnosticNumQuestions, setDiagnosticNumQuestions] = useState(10);
  const [formData, setFormData] = useState({
    testName: '',
    levelId: '',
    oldLessonCode: '', // For filtering questions
    newLessonUrl: '', // For redirect (e.g., "1.1a")
    testTypes: {
      vocab: false,
      grammar: false,
      comprehension: false
    },
    numberOfQuestions: 15 // For placement tests
  });

  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const levels = [
    { id: 'level-1', name: 'Level 1 - Roots' },
    { id: 'level-2', name: 'Level 2 - Branches' },
    { id: 'level-3', name: 'Level 3 - Skies' },
    { id: 'level-4', name: 'Level 4 - Rivers' },
    { id: 'level-5', name: 'Level 5 - Mountains' }
  ];

  // Generate OLD lesson codes for filtering questions
  const getOldLessonCodes = () => {
    if (!formData.levelId) return [];
    
    const levelNum = formData.levelId.split('-')[1];
    const lessons = [];
    
    for (let i = 1; i <= 10; i++) {
      lessons.push({
        code: `${levelNum}.${i}`,
        label: `Old Lesson ${levelNum}.${i}`
      });
    }
    
    return lessons;
  };

  const isDiagnosticTest = () => formData.oldLessonCode !== '';

  const handleLevelChange = (newLevelId) => {
    setFormData({
      ...formData,
      levelId: newLevelId,
      oldLessonCode: '',
      newLessonUrl: ''
    });
    setAvailableQuestions([]);
    setSelectedQuestions([]);
  };

  const handleTestTypeChange = (type) => {
    setFormData({
      ...formData,
      testTypes: {
        ...formData.testTypes,
        [type]: !formData.testTypes[type]
      }
    });
  };
  // Load questions when filters change (for diagnostic tests)
  useEffect(() => {
    if (isDiagnosticTest() && formData.levelId) {
      loadQuestionsForSelection();
    }
  }, [formData.levelId, formData.oldLessonCode, formData.testTypes]);

  // Validation for selected questions
    useEffect(() => {
      if (selectedQuestions.length > 0 && selectedQuestions.length < 10) {
        setError('Please select at least 10 questions');
      } else {
        setError('');
      }
    }, [selectedQuestions.length]);

  const loadQuestionsForSelection = async () => {
    try {
      const selectedTypes = Object.keys(formData.testTypes).filter(
        type => formData.testTypes[type]
      );

      if (selectedTypes.length === 0) {
        setAvailableQuestions([]);
        return;
      }

      let allQuestions = [];

      for (const testType of selectedTypes) {
        const q = query(
          collection(db, 'testQuestions'),
          where('levelId', '==', formData.levelId),
          where('testType', '==', testType),
          where('lessonCode', '==', formData.oldLessonCode)
        );

        const snapshot = await getDocs(q);
        const questions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        allQuestions = [...allQuestions, ...questions];
      }

      setAvailableQuestions(allQuestions);
    } catch (err) {
      console.error('Error loading questions:', err);
      setError('Failed to load questions');
    }
  };

  const handleQuestionToggle = (questionId) => {
    setSelectedQuestions(prev => {
      if (prev.includes(questionId)) {
        return prev.filter(id => id !== questionId);
      } else {
        if (prev.length >= 15) {
          setError('Maximum 15 questions allowed');
          return prev;
        }
        return [...prev, questionId];
      }
    });
  };

  const validateStep1 = () => {
    if (!formData.testName.trim()) {
      setError('Please enter a test name');
      return false;
    }

    if (!formData.levelId) {
      setError('Please select a level');
      return false;
    }

    const selectedTypes = Object.keys(formData.testTypes).filter(
      type => formData.testTypes[type]
    );

    if (selectedTypes.length === 0) {
      setError('Please select at least one test type');
      return false;
    }

    // For diagnostic tests
    if (isDiagnosticTest()) {
      if (!formData.newLessonUrl.trim()) {
        setError('Please enter the new lesson URL (e.g., 1.1a)');
        return false;
      }
    } else {
      // For placement tests
      if (formData.numberOfQuestions < 1) {
        setError('Number of questions must be at least 1');
        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    setError('');
    
    if (!validateStep1()) return;

    if (isDiagnosticTest()) {
      // Go to question selection step
      setStep(2);
    } else {
      // For placement tests, create immediately
      handleSubmit();
    }
  };

  const handleBack = () => {
    setStep(1);
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // Validate for diagnostic tests
    if (isDiagnosticTest() && selectedQuestions.length === 0) {
      setError('Please select at least one question');
      return;
    }

    setLoading(true);

    try {
      const selectedTypes = Object.keys(formData.testTypes).filter(
        type => formData.testTypes[type]
      );

      const mode = isDiagnosticTest() ? 'diagnostic' : 'placement';

      if (mode === 'placement') {
        // PLACEMENT: Create separate tests for each type (old logic)
        for (const testType of selectedTypes) {
          const testData = {
            levelId: formData.levelId,
            testType: testType,
            lessonCode: null,
            title: `${formData.testName} - ${testType.charAt(0).toUpperCase() + testType.slice(1)}`,
            mode: mode,
            settings: {
              numberOfQuestions: 10,
              passThreshold: 80,
              showResults: true,
              showCorrectAnswers: true,
              scoringEnabled: true
            },
            performanceMessages: {
              low: "Keep practicing! Review the materials and try again.",
              medium: "Good effort! A bit more practice and you'll master this.",
              high: "Excellent work! You've mastered this level."
            },
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          await addDoc(collection(db, 'tests'), testData);
        }
        setSuccess(`Successfully created ${selectedTypes.length} placement test(s)!`);
      } else {
        // DIAGNOSTIC: Create ONE test with selected questions
        const testData = {
        levelId: formData.levelId,
        testTypes: selectedTypes,
        oldLessonCode: formData.oldLessonCode,
        newLessonUrl: formData.newLessonUrl,
        title: formData.testName,
        mode: mode,
        selectedQuestionIds: selectedQuestions, // Pool of questions
        settings: {
          numberOfQuestions: diagnosticNumQuestions, // How many to use each time
          passThreshold: 80,
          showResults: true,
          showCorrectAnswers: true,
          scoringEnabled: false
        },
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        await addDoc(collection(db, 'tests'), testData);
        setSuccess('Successfully created diagnostic test!');
      }

      // Reset form
      setFormData({
        testName: '',
        levelId: '',
        oldLessonCode: '',
        newLessonUrl: '',
        testTypes: {
          vocab: false,
          grammar: false,
          comprehension: false
        },
        numberOfQuestions: 15
      });
      setStep(1);
      setAvailableQuestions([]);
      setSelectedQuestions([]);

      if (onTestCreated) {
        onTestCreated();
      }

      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error creating test:', err);
      setError('Failed to create test. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getQuestionTypeLabel = (question) => {
    const typeLabels = {
      'mcq': 'MCQ',
      'multiple-response': 'Multiple Response',
      'type-in': 'Type In',
      'order': 'Order',
      'matching': 'Matching'
    };
    return typeLabels[question.questionType] || question.questionType;
  };

 const getQuestionPreview = (question) => {
  const content = question.content || {};
  
  // Helper function to clean HTML and get text
  const cleanHTML = (html) => {
    if (!html) return '';
    // Remove all HTML tags and decode entities
    let text = html
      .replace(/<br\s*\/?>/gi, ' ')  // Replace <br> with space
      .replace(/<[^>]*>/g, '')        // Remove all HTML tags
      .replace(/&nbsp;/g, ' ')        // Replace &nbsp; with space
      .replace(/\s+/g, ' ')           // Collapse multiple spaces
      .trim();                        // Trim whitespace
    return text;
  };
  
  // MCQ - show stem
  if (content.stem) {
    const text = cleanHTML(content.stem);
    if (text) return text.substring(0, 100) + (text.length > 100 ? '...' : '');
  }
  
  // Type-in - show text
  if (content.text) {
    const text = cleanHTML(content.text);
    if (text) return text.substring(0, 100) + (text.length > 100 ? '...' : '');
  }
  
  // Order/Matching - show instructions
  if (content.instructions) {
    const text = cleanHTML(content.instructions);
    if (text) return text.substring(0, 100) + (text.length > 100 ? '...' : '');
  }
  
  // Order - show first item
  if (content.items && content.items.length > 0) {
    const firstItem = content.items[0].text || '';
    return `Order: ${firstItem.substring(0, 80)}${firstItem.length > 80 ? '...' : ''}`;
  }
  
  // Matching - show first pair
  if (content.pairs && content.pairs.length > 0) {
    return `Match: ${content.pairs[0].left} → ${content.pairs[0].right}`;
  }
  
  return '[No preview available]';
};

  // Step 1: Basic Info
  if (step === 1) {
    return (
      <div className="test-builder">
        <h2>Create New Test</h2>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form className="test-form" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
          <div className="form-group">
            <label htmlFor="testName">Test Name *</label>
            <input
              id="testName"
              type="text"
              value={formData.testName}
              onChange={(e) => setFormData({ ...formData, testName: e.target.value })}
              placeholder="e.g., Lesson 1.1a - Greetings"
              required
            />
            <small>For diagnostic tests, use the NEW lesson structure name</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="level">Level *</label>
              <select
                id="level"
                value={formData.levelId}
                onChange={(e) => handleLevelChange(e.target.value)}
                required
              >
                <option value="">Select Level</option>
                {levels.map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="oldLesson">Old Lesson (Optional for Diagnostic)</label>
              <select
                id="oldLesson"
                value={formData.oldLessonCode}
                onChange={(e) => setFormData({ ...formData, oldLessonCode: e.target.value })}
                disabled={!formData.levelId}
              >
                <option value="">None (Placement Test)</option>
                {getOldLessonCodes().map(lesson => (
                  <option key={lesson.code} value={lesson.code}>
                    {lesson.label}
                  </option>
                ))}
              </select>
              <small>
                {formData.oldLessonCode ? '📍 Diagnostic test - will select questions' : '📊 Placement test - random questions'}
              </small>
            </div>
          </div>

          {isDiagnosticTest() && (
            <div className="form-group">
              <label htmlFor="newLessonUrl">New Lesson URL *</label>
              <input
                id="newLessonUrl"
                type="text"
                value={formData.newLessonUrl}
                onChange={(e) => setFormData({ ...formData, newLessonUrl: e.target.value })}
                placeholder="e.g., 1.1a"
                required
              />
              <small>Enter the NEW lesson code for redirect (e.g., 1.1a, 2.3b)</small>
            </div>
          )}

          <div className="form-group">
            <label>Test Type(s) *</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.testTypes.vocab}
                  onChange={() => handleTestTypeChange('vocab')}
                />
                Vocabulary
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.testTypes.grammar}
                  onChange={() => handleTestTypeChange('grammar')}
                />
                Grammar
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.testTypes.comprehension}
                  onChange={() => handleTestTypeChange('comprehension')}
                />
                Comprehension
              </label>
            </div>
            <small>
              {isDiagnosticTest()
                ? 'Questions from selected types will be shown for selection'
                : 'Separate placement tests will be created for each type'}
            </small>
          </div>

          {!isDiagnosticTest() && (
            <div className="form-group">
              <label htmlFor="numberOfQuestions">Number of Questions *</label>
              <input
                id="numberOfQuestions"
                type="number"
                min="1"
                max="50"
                value={formData.numberOfQuestions}
                onChange={(e) => setFormData({ ...formData, numberOfQuestions: parseInt(e.target.value) || 1 })}
                required
              />
              <small>Questions will be randomly selected from the question bank</small>
            </div>
          )}

          <div className="info-box">
            <strong>ℹ️ Test Info:</strong>
            <ul>
              <li>Mode: {isDiagnosticTest() ? 'Diagnostic' : 'Placement'}</li>
              <li>Pass threshold: 80%</li>
              <li>Results: Shown immediately after submission</li>
              <li>Correct answers: Always shown (for learning)</li>
              {isDiagnosticTest() && (
                <li>Redirect URL: learn.litvill.com/lesson/{formData.newLessonUrl || '[url]'}</li>
              )}
            </ul>
          </div>

          <div className="form-actions">
            <button type="submit" className="create-btn" disabled={loading}>
              {isDiagnosticTest() ? 'Next: Select Questions →' : 'Create Placement Test(s)'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Step 2: Question Selection (Diagnostic only)
  return (
    <div className="test-builder">
      <h2>Select Questions for: {formData.testName}</h2>
      <p className="section-description">
        Select questions from Old Lesson {formData.oldLessonCode}
      </p>

      {error && <div className="error-message">{error}</div>}

      <div className="selection-summary">
        <strong>Selected: {selectedQuestions.length} questions</strong>
        <p>Select at least 10 questions. Tests will use 10 random questions from your selection.</p>
      </div>

      <div className="questions-list">
        {availableQuestions.length === 0 ? (
          <div className="empty-message">
            No questions found. Make sure questions exist for the selected lesson and test types.
          </div>
        ) : (
          availableQuestions.map(question => (
            <div
              key={question.id}
              className={`question-item ${selectedQuestions.includes(question.id) ? 'selected' : ''}`}
            >
              <div className="question-checkbox">
                <input
                  type="checkbox"
                  checked={selectedQuestions.includes(question.id)}
                  onChange={() => handleQuestionToggle(question.id)}
                  disabled={!selectedQuestions.includes(question.id) && selectedQuestions.length >= 15}
                />
              </div>
              <div className="question-info">
                <div className="question-header">
                  <span className="question-type-badge">{getQuestionTypeLabel(question)}</span>
                  <span className="question-test-type">{question.testType}</span>
                </div>
                <div className="question-preview">
                  {getQuestionPreview(question)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* After question selection, before footer */}
      <div className="test-settings-section">
        <h3>Test Settings</h3>
        <div className="form-group">
          <label htmlFor="numQuestions">
            Number of Questions to Use Per Test
          </label>
          <input
            id="numQuestions"
            type="number"
            min="1"
            max={selectedQuestions.length}
            value={diagnosticNumQuestions}
            onChange={(e) => setDiagnosticNumQuestions(parseInt(e.target.value) || 1)}
          />
          <small>
            Diagnostic tests use 10 questions. Select at least 10 questions to create variety. 
            Each test will randomly pick 10 from your selection.
          </small>
        </div>
      </div>
      <div className="form-actions">
        <button onClick={handleBack} className="back-btn" disabled={loading}>
          ← Back
        </button>
        <button
          onClick={handleSubmit}
          className="create-btn"
          disabled={loading || selectedQuestions.length === 0}
        >
          {loading ? 'Creating...' : `Create Test with ${selectedQuestions.length} Questions`}
        </button>
      </div>
    </div>
  );
};

export default TestBuilder;